import { useCallback, useMemo, useState } from 'react';
import { Table, ButtonVariants } from '@carboledger/ui';
import { Plus } from '@carboledger/ui/icons';
import { useTranslations } from '@/hooks';
import { downloadAttachment } from '@/lib/utils';
import { useRemoveAttachmentMutation } from '@/lib/api/attachmentsApi';
import emptyBoxIllustration from '@/assets/illustrations/empty-box.png';
import { AddAttachmentModal, buildAttachmentsColumns } from '../AddAttachmentModal';

const getAttachmentId = (row) => row.id;

/**
 * Props:
 *   attachments  rows for the table — each carries `fileId` and `name`
 *   target       ATTACHMENT_TARGETS key — which record type the files belong to
 *   module       the active module route segment
 *   entityId     the record's database id
 *   modalTitle   AddAttachmentModal's title (pre-interpolated)
 *   addLabel     the add button's label (pre-translated) — screens word it differently
 */
export const AttachmentsTabView = ({
  attachments,
  target,
  module,
  entityId,
  modalTitle,
  addLabel,
}) => {
  const t = useTranslations();
  const [isAddAttachmentOpen, setIsAddAttachmentOpen] = useState(false);
  const [removeAttachment] = useRemoveAttachmentMutation();

  const handleOpenAddAttachment = useCallback(() => setIsAddAttachmentOpen(true), []);
  const handleCloseAddAttachment = useCallback(() => setIsAddAttachmentOpen(false), []);

  const handleRemove = useCallback(
    (row) => removeAttachment({ target, module, id: entityId, fileId: row.fileId }),
    [removeAttachment, target, module, entityId],
  );
  const columns = useMemo(() => buildAttachmentsColumns(t), [t]);

  const rowActions = useMemo(
    () => [
      {
        id: 'download',
        label: t('ATTACHMENT_ACTION_DOWNLOAD'),
        variant: ButtonVariants.tertiary,
        onClick: downloadAttachment,
      },
      {
        id: 'remove',
        label: t('COMMON_REMOVE'),
        variant: ButtonVariants.tertiary,
        onClick: handleRemove,
      },
    ],
    [t, handleRemove],
  );

  // Outlined here; the empty state's own CTA is the filled one.
  const toolbarActions = useMemo(
    () => [
      {
        id: 'add',
        label: addLabel,
        icon: Plus,
        variant: ButtonVariants.secondary,
        onClick: handleOpenAddAttachment,
      },
    ],
    [addLabel, handleOpenAddAttachment],
  );

  const emptyState = useMemo(
    () => ({
      image: <img src={emptyBoxIllustration} alt="" width={200} height={200} />,
      title: t('ATTACHMENTS_EMPTY_TITLE'),
      description: t('ATTACHMENTS_EMPTY_DESCRIPTION'),
      primaryLabel: addLabel,
      onPrimaryClick: handleOpenAddAttachment,
      primaryIcon: <Plus />,
    }),
    [t, addLabel, handleOpenAddAttachment],
  );

  return (
    <>
      <Table
        columns={columns}
        data={attachments}
        getRowId={getAttachmentId}
        toolbarActions={toolbarActions}
        rowActions={rowActions}
        actionsHeader={t('FIELD_ACTION')}
        emptyState={emptyState}
      />

      <AddAttachmentModal
        open={isAddAttachmentOpen}
        onClose={handleCloseAddAttachment}
        title={modalTitle}
        target={target}
        module={module}
        entityId={entityId}
      />
    </>
  );
};
